import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, watch, getCurrentInstance } from 'vue';
import { CurrentLiveStream, SeriesResponse, SeriesVideoOrLiveStreamResponse, VideoResponseWithProgress } from '@/types/cms';
import { navigationService } from 'vue-spatialnavigation';
import { focusElementById } from '@/helpers/focus';
import { toDesignPx } from '@/helpers/scale';

import SeriesCell from '@/components/cells/SeriesCell.vue';
import EpisodeCell from '@/components/cells/EpisodeCell.vue';
import LiveStreamCell from "@/components/cells/LiveStreamCell.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'HorizontalScroller',
  props: {
    containerId: null,
    itemsPrefix: null,
    showOverlay: { type: Boolean, default: false },
    showSeriesAsBoxset: { type: Boolean, default: false },
    showCategoryOnSeries: { type: Boolean, default: false },
    items: null,
    series: null,
    videos: null,
    liveStreams: null,
    textColor: null
  },
  emits: ["handleUp", "handleDown", "handleLeftFallback", "handleRightFallback"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'handleUp'): void,
  (e: 'handleDown'): void,
  (e: 'handleLeftFallback'): void,
  (e: 'handleRightFallback'): void,
}), expose: any, slots: any, attrs: any }) {

const props = __props as { containerId: string, itemsPrefix: string, showOverlay: boolean, showSeriesAsBoxset: boolean, showCategoryOnSeries: boolean, items?: SeriesVideoOrLiveStreamResponse[], series?: SeriesResponse[], videos?: VideoResponseWithProgress[], liveStreams?: CurrentLiveStream[], textColor?: string };


const SCROLL_BACK_THRESHOLD = 400;





const instance = getCurrentInstance();
const lazyLoad = instance?.proxy?.$lazyLoad;

const resolvedItems = computed<SeriesVideoOrLiveStreamResponse[]>(() => {
  if (props.items) return props.items;
  if (props.series) return props.series.map((series) => ({ type: 'SERIES', series }));
  if (props.videos) return props.videos.map((video) => ({ type: 'VIDEO', video }));
  if (props.liveStreams) return props.liveStreams.map((stream) => ({ type: 'LIVE_STREAM', stream }));
  return [];
});

const itemId = (index: number) => `${props.itemsPrefix}-${index}`;

/**
 * `getBoundingClientRect` reports pixels after the design canvas's transform, so
 * on a TV that scales the canvas down the raw value would cross the threshold
 * early. `toDesignPx` puts it back in the units the constant is written in.
 */
const isScrolledBack = (element: HTMLElement) =>
  toDesignPx(element.getBoundingClientRect().left) < SCROLL_BACK_THRESHOLD;

const moveFocusSideways = (index: number, alwaysScroll: boolean) => {
  const targetId = itemId(index);
  if (!navigationService.getFocusElementById(targetId)) return;

  const container = document.getElementById(props.containerId);
  const target = document.getElementById(targetId);

  if (container && target && (alwaysScroll || isScrolledBack(target))) {
    container.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
  }

  //Call helper with no scrolling - already managed above
  focusElementById(targetId);
};

const handleRight = (index: number) => {
  if (index < resolvedItems.value.length - 1) {
    moveFocusSideways(index + 1, true);
    return;
  }
  emit('handleRightFallback');
};

const handleLeft = (index: number) => {
  if (index === 0) {
    emit('handleLeftFallback');
    return;
  }
  moveFocusSideways(index - 1, false);
};


const scanForLazyImages = () => {
  setTimeout(() => {
    const container = document.getElementById(props.containerId);
    if (container) lazyLoad?.(container);
  }, 200);
};

onMounted(() => {
  scanForLazyImages();
});

//We want to watch for mutations to the items so we reload. Rails are keyed of ID so not necessarily remounted on changes
watch(() => resolvedItems.value.map((item) => {
  switch (item.type) {
    case 'SERIES':
      return item.series.slug;
    case 'VIDEO':
      return item.video.vid;
    case 'LIVE_STREAM':
      return item.stream.currentShow?.startTime //Current show start time as unique ID for current item
  }
}).join(), scanForLazyImages);


return { __sfc: true,SCROLL_BACK_THRESHOLD, props, emit, instance, lazyLoad, resolvedItems, itemId, isScrolledBack, moveFocusSideways, handleRight, handleLeft, scanForLazyImages, SeriesCell, EpisodeCell, LiveStreamCell }
}

})